import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Dropdown,
    Image,
    Input,
    Popconfirm,
    Select,
    Space,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import {
    DeleteOutlined,
    GiftOutlined,
    ReloadOutlined,
    RollbackOutlined,
    SearchOutlined,
    SettingOutlined,
} from '@ant-design/icons';
import { axiosInstance } from '../config';
import { promoProfitLoss } from '../components/Cost/costCalc';
import './OzonActions.css';

const moneyFormat = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 2,
});

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
        ? String(value)
        : date.toLocaleString('ru-RU', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
          });
};

const TABLE_SETTINGS_KEY = 'ozonActionsTableSettings';
const EMPTY_TABLE_STATE = { hiddenColumns: [], columnWidths: {} };

const ColumnTitle = ({ title, hint }) => (
    <div className="leading-4">
        <div>{title}</div>
        {hint ? <div className="text-[11px] font-normal text-slate-400">{hint}</div> : null}
    </div>
);

const PRICE_OZON_TITLE = <ColumnTitle title="Цена" hint="(в Озон)" />;
const DISCOUNT_OZON_TITLE = <ColumnTitle title="Скидка" hint="(по версии Озон)" />;

const PRODUCT_COLUMN_OPTIONS = [
    { key: 'product', label: 'Товар' },
    { key: 'product_id', label: 'ID' },
    { key: 'seller_price', label: 'Исходная цена' },
    { key: 'price', label: 'Цена (в Озон)' },
    { key: 'action_price', label: 'Цена акции' },
    { key: 'discount_percent', label: 'Скидка (по версии Озон)' },
    { key: 'seller_over_action_percent', label: 'Реальная скидка' },
    { key: 'margin_loss', label: 'Потеря маржи' },
    { key: 'profit_loss', label: 'Потеря руб' },
    { key: 'max_action_price', label: 'Макс. цена' },
    { key: 'add_mode', label: 'Добавлен' },
    { key: 'warehouse_stock', label: 'На складе Ozon | на моём складе' },
    { key: 'is_quarantined', label: 'Карантин' },
    { key: 'actions', label: 'Действия' },
];

const CANDIDATE_COLUMN_OPTIONS = [
    { key: 'product', label: 'Товар' },
    { key: 'product_id', label: 'ID' },
    { key: 'seller_price', label: 'Исходная цена' },
    { key: 'price', label: 'Цена (в Озон)' },
    { key: 'action_price', label: 'Цена автодобавления' },
    { key: 'discount_percent', label: 'Скидка (по версии Озон)' },
    { key: 'seller_over_action_percent', label: 'Реальная скидка' },
    { key: 'margin_loss', label: 'Потеря маржи' },
    { key: 'profit_loss', label: 'Потеря руб' },
    { key: 'warehouse_stock', label: 'На складе Ozon | на моём складе' },
    { key: 'quantity_to_auto_add', label: 'Кол-во' },
    { key: 'will_be_quarantined', label: 'Карантин' },
    { key: 'actions', label: 'Действия' },
];

const ACTION_COLUMN_OPTIONS = [
    { key: 'title', label: 'Акция' },
    { key: 'period', label: 'Период' },
    { key: 'participating_products_count', label: 'В акции' },
    { key: 'potential_products_count', label: 'Кандидаты' },
    { key: 'auto_add', label: 'Автодобавление' },
    { key: 'is_participating', label: 'Участие' },
];

const normalizeTableState = (value) => {
    const columnWidths =
        value?.columnWidths && typeof value.columnWidths === 'object'
            ? Object.fromEntries(
                  Object.entries(value.columnWidths).filter(
                      ([, width]) => typeof width === 'number' && width >= 40
                  )
              )
            : {};
    return {
        hiddenColumns: Array.isArray(value?.hiddenColumns)
            ? value.hiddenColumns.filter((key) => typeof key === 'string')
            : [],
        columnWidths,
    };
};

const loadAllTableSettings = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(TABLE_SETTINGS_KEY) || '{}');
        return {
            products: normalizeTableState(parsed.products),
            candidates: normalizeTableState(parsed.candidates),
            actions: normalizeTableState(parsed.actions),
        };
    } catch {
        return {
            products: { ...EMPTY_TABLE_STATE, columnWidths: {} },
            candidates: { ...EMPTY_TABLE_STATE, columnWidths: {} },
            actions: { ...EMPTY_TABLE_STATE, columnWidths: {} },
        };
    }
};

const ResizableTitle = (props) => {
    const { onResize, onResizeStop, onResetWidth, width, ...restProps } = props;
    if (!width || !onResize) {
        return <th {...restProps} />;
    }
    return (
        <th
            {...restProps}
            style={{ ...restProps.style, width, minWidth: width, maxWidth: width, position: 'relative' }}
        >
            {restProps.children}
            <span
                className="ozon-col-resize-handle"
                title="Потяните, чтобы изменить ширину. Двойной клик — сбросить"
                onClick={(event) => event.stopPropagation()}
                onDoubleClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onResetWidth?.();
                }}
                onPointerDown={(event) => {
                    if (event.button !== 0) return;
                    event.preventDefault();
                    event.stopPropagation();
                    const startX = event.clientX;
                    const startWidth = width;
                    let lastWidth = startWidth;
                    let frame = 0;
                    const applyWidth = (nextWidth) => {
                        lastWidth = Math.max(48, nextWidth);
                        if (frame) cancelAnimationFrame(frame);
                        frame = requestAnimationFrame(() => onResize(lastWidth));
                    };
                    const onPointerMove = (moveEvent) => {
                        applyWidth(startWidth + moveEvent.clientX - startX);
                    };
                    const onPointerUp = () => {
                        if (frame) cancelAnimationFrame(frame);
                        document.removeEventListener('pointermove', onPointerMove);
                        document.removeEventListener('pointerup', onPointerUp);
                        document.body.classList.remove('ozon-col-resizing');
                        onResizeStop?.(lastWidth);
                    };
                    document.body.classList.add('ozon-col-resizing');
                    document.addEventListener('pointermove', onPointerMove);
                    document.addEventListener('pointerup', onPointerUp);
                }}
            />
        </th>
    );
};

const applyColumnLayout = (columns, settings, onResize, onResizeStop, onResetWidth) =>
    columns
        .filter((column) => !settings.hiddenColumns.includes(column.key))
        .map((column) => {
            const saved = settings.columnWidths?.[column.key];
            const width =
                typeof saved === 'number' && saved >= 40 ? saved : column.width;
            const prevHeader = column.onHeaderCell;
            const prevCell = column.onCell;
            return {
                ...column,
                width,
                onHeaderCell: () => {
                    const extra = prevHeader ? prevHeader() : {};
                    return {
                        ...extra,
                        width,
                        onResize: (nextWidth) => onResize(column.key, nextWidth),
                        onResizeStop: (nextWidth) => onResizeStop(column.key, nextWidth),
                        onResetWidth: () => onResetWidth(column.key),
                    };
                },
                onCell: (record, index) => {
                    const extra = prevCell ? prevCell(record, index) : {};
                    return {
                        ...extra,
                        style: {
                            ...extra.style,
                            width,
                            minWidth: width,
                            maxWidth: width,
                        },
                    };
                },
            };
        });

const ColumnsMenu = ({ options, hiddenColumns, onChange }) => (
    <Dropdown
        trigger={['click']}
        dropdownRender={() => (
            <div
                className="rounded-lg bg-white p-3 shadow-lg"
                style={{ minWidth: 260 }}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className="mb-2 text-xs font-medium text-slate-500">Колонки</div>
                <Checkbox.Group
                    value={options
                        .map((option) => option.key)
                        .filter((key) => !hiddenColumns.includes(key))}
                    onChange={(visibleKeys) => {
                        const hidden = options
                            .map((option) => option.key)
                            .filter((key) => !visibleKeys.includes(key));
                        if (hidden.length >= options.length) return;
                        onChange(hidden);
                    }}
                >
                    <div className="flex max-h-80 flex-col gap-1.5 overflow-auto">
                        {options.map((option) => (
                            <Checkbox key={option.key} value={option.key}>
                                {option.label}
                            </Checkbox>
                        ))}
                    </div>
                </Checkbox.Group>
                <Button type="link" size="small" className="mt-2 px-0" onClick={() => onChange([])}>
                    Показать все
                </Button>
            </div>
        )}
    >
        <Button icon={<SettingOutlined />}>Колонки</Button>
    </Dropdown>
);

const productImageSrc = (value) =>
    typeof value === 'string' && /^https?:\/\//i.test(value) ? value : '';

const ProductCell = ({ record }) => {
    const name = record.name || 'Без названия';
    const image = productImageSrc(record.primary_image);
    return (
        <div className="ozon-actions-product">
            {image ? (
                <Image
                    src={image}
                    width={44}
                    height={44}
                    className="rounded object-cover"
                    preview
                />
            ) : null}
            <div className="ozon-actions-product__text">
                <Typography.Text strong className="ozon-actions-product__name">
                    {name}
                </Typography.Text>
                <Typography.Text type="secondary" copyable={Boolean(record.offer_id)}>
                    {record.offer_id || '—'}
                </Typography.Text>
            </div>
        </div>
    );
};

const formatMoney = (value) => {
    const raw = value && typeof value === 'object' ? value.value ?? value.amount : value;
    if (raw === null || raw === undefined || raw === '') return '—';
    const number = Number(raw);
    return Number.isNaN(number) ? String(raw) : moneyFormat.format(number);
};

const addModeMeta = {
    AUTO: { label: 'Авто', color: 'blue' },
    SELLER: { label: 'Продавец', color: 'green' },
};

const SELLER_PRICE_CELL = { background: '#fff7ed' };
const SELLER_PRICE_HEADER = { background: '#ffedd5', color: '#9a3412', fontWeight: 600 };

const formatPercent = (value) => {
    if (value == null || Number.isNaN(Number(value))) return null;
    return `${String(Number(value)).replace('.', ',')}%`;
};

const SellerPriceCell = ({ record }) => {
    if (record?.seller_price == null) return '—';
    return <div className="font-semibold text-orange-800">{formatMoney(record.seller_price)}</div>;
};

const RealDiscountCell = ({ record }) => {
    const percent = formatPercent(record?.seller_over_action_percent);
    if (!percent) return '—';
    const tone =
        record.seller_over_action_percent > 0
            ? 'text-emerald-700'
            : record.seller_over_action_percent < 0
              ? 'text-rose-600'
              : 'text-slate-500';
    return <span className={`font-semibold ${tone}`}>{percent}</span>;
};

const LOSS_CELL = { background: '#fff1f2' };
const LOSS_HEADER = { background: '#ffe4e6', color: '#9f1239', fontWeight: 600 };

const signedMoney = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return '—';
    const formatted = moneyFormat.format(Math.abs(number));
    if (number > 0) return `+${formatted}`;
    if (number < 0) return `−${formatted}`;
    return formatted;
};

const signedPercent = (value) => {
    if (value == null || Number.isNaN(Number(value))) return null;
    const number = Number(value);
    const text = `${String(Math.abs(number).toFixed(1)).replace('.', ',')}%`;
    if (number > 0) return `+${text}`;
    if (number < 0) return `−${text}`;
    return text;
};

const lossTone = (value) =>
    value < 0 ? 'text-rose-700' : value > 0 ? 'text-emerald-700' : 'text-slate-500';

const sellerPriceColumn = {
    title: 'Исходная цена',
    key: 'seller_price',
    width: 140,
    onHeaderCell: () => ({ style: SELLER_PRICE_HEADER }),
    onCell: () => ({ style: SELLER_PRICE_CELL }),
    render: (_, record) => <SellerPriceCell record={record} />,
};

const warehouseStockColumn = {
    title: (
        <div className="leading-4">
            <div>На складе Ozon |</div>
            <div>на моём складе</div>
        </div>
    ),
    key: 'warehouse_stock',
    width: 150,
    align: 'center',
    render: (_, record) => {
        const ozon = record.stock_ozon;
        const mine = record.stock_my;
        if (ozon == null && mine == null) return '—';
        return (
            <span className="tabular-nums text-slate-700">
                {ozon ?? 0}
                <span className="px-1 text-slate-400">|</span>
                {mine ?? 0}
            </span>
        );
    },
};

const realDiscountColumn = {
    title: 'Реальная скидка',
    key: 'seller_over_action_percent',
    width: 140,
    onHeaderCell: () => ({ style: SELLER_PRICE_HEADER }),
    onCell: () => ({ style: SELLER_PRICE_CELL }),
    render: (_, record) => <RealDiscountCell record={record} />,
};

const profitLossColumns = (costItems, catalog) => [
    {
        title: 'Потеря маржи',
        key: 'margin_loss',
        width: 140,
        onHeaderCell: () => ({ style: LOSS_HEADER }),
        onCell: () => ({ style: LOSS_CELL }),
        render: (_, record) => {
            const loss = promoProfitLoss(record, costItems, catalog);
            const text = signedPercent(loss?.marginLossPercent);
            if (!text) return '—';
            return (
                <span className={`text-sm font-semibold ${lossTone(loss.marginLossPercent)}`}>
                    {text}
                </span>
            );
        },
    },
    {
        title: 'Потеря руб',
        key: 'profit_loss',
        width: 260,
        onHeaderCell: () => ({ style: LOSS_HEADER }),
        onCell: () => ({ style: LOSS_CELL }),
        render: (_, record) => {
            const loss = promoProfitLoss(record, costItems, catalog);
            if (!loss) return '—';
            return (
                <div className={`text-sm leading-5 ${lossTone(loss.profitDiff)}`}>
                    <div>
                        {formatMoney(loss.oldProfit)} → {formatMoney(loss.newProfit)}
                    </div>
                    <div className="font-semibold">({signedMoney(loss.profitDiff)})</div>
                </div>
            );
        },
    },
];

const OzonActions = () => {
    const [actions, setActions] = useState([]);
    const [summary, setSummary] = useState({ total: 0, participating: 0, with_auto_add: 0 });
    const [actionsLoading, setActionsLoading] = useState(false);
    const [actionQuery, setActionQuery] = useState('');
    const [actionFilter, setActionFilter] = useState('all');
    const [selectedActionId, setSelectedActionId] = useState(null);

    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [productQuery, setProductQuery] = useState('');

    const [candidates, setCandidates] = useState([]);
    const [candidatesLoading, setCandidatesLoading] = useState(false);
    const [candidateQuery, setCandidateQuery] = useState('');
    const [autoAddDate, setAutoAddDate] = useState('');
    const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
    const [deleting, setDeleting] = useState(false);
    const [resettingProductKey, setResettingProductKey] = useState('');
    const [activeTab, setActiveTab] = useState('products');
    const [costItems, setCostItems] = useState([]);
    const [costCatalog, setCostCatalog] = useState([]);
    const [tableSettings, setTableSettings] = useState(loadAllTableSettings);

    const persistTableSettings = (next) => {
        localStorage.setItem(TABLE_SETTINGS_KEY, JSON.stringify(next));
    };

    const patchTableSettings = (tableKey, patch, persist = true) => {
        setTableSettings((current) => {
            const next = {
                ...current,
                [tableKey]: {
                    ...current[tableKey],
                    ...patch,
                    columnWidths:
                        patch.columnWidths !== undefined
                            ? patch.columnWidths
                            : current[tableKey].columnWidths,
                },
            };
            if (persist) persistTableSettings(next);
            return next;
        });
    };

    const updateColumnWidth = (tableKey, key, width, persist = true) => {
        setTableSettings((current) => {
            const next = {
                ...current,
                [tableKey]: {
                    ...current[tableKey],
                    columnWidths: {
                        ...(current[tableKey].columnWidths || {}),
                        [key]: width,
                    },
                },
            };
            if (persist) persistTableSettings(next);
            return next;
        });
    };

    const resetColumnWidth = (tableKey, key) => {
        setTableSettings((current) => {
            const columnWidths = { ...(current[tableKey].columnWidths || {}) };
            delete columnWidths[key];
            const next = {
                ...current,
                [tableKey]: { ...current[tableKey], columnWidths },
            };
            persistTableSettings(next);
            return next;
        });
    };

    const setHiddenColumns = (tableKey, hiddenColumns) => {
        patchTableSettings(tableKey, { hiddenColumns });
    };

    const selectedAction = useMemo(
        () => actions.find((item) => item.id === selectedActionId) || null,
        [actions, selectedActionId]
    );

    const loadActions = useCallback(async () => {
        setActionsLoading(true);
        try {
            const { data } = await axiosInstance.get('/v1/actions/ozon');
            const nextActions = data.actions || [];
            setActions(nextActions);
            setSummary({
                total: data.total || nextActions.length,
                participating: data.participating || 0,
                with_auto_add: data.with_auto_add || 0,
            });
            setSelectedActionId((current) => {
                if (current && nextActions.some((item) => item.id === current)) {
                    return current;
                }
                const preferred =
                    nextActions.find((item) => item.is_participating) ||
                    nextActions.find((item) => item.has_auto_add) ||
                    nextActions[0];
                return preferred?.id ?? null;
            });
        } catch (error) {
            message.error(error.response?.data?.detail || 'Не удалось загрузить акции Ozon');
        } finally {
            setActionsLoading(false);
        }
    }, []);

    const loadProducts = useCallback(async (actionId) => {
        if (!actionId) {
            setProducts([]);
            return;
        }
        setProductsLoading(true);
        try {
            const { data } = await axiosInstance.get(`/v1/actions/ozon/${actionId}/products`, {
                params: { fetch_all: true },
            });
            setProducts(data.products || []);
        } catch (error) {
            setProducts([]);
            message.error(
                error.response?.data?.detail || 'Не удалось загрузить товары акции'
            );
        } finally {
            setProductsLoading(false);
        }
    }, []);

    const loadCandidates = useCallback(async (actionId, date) => {
        if (!actionId || !date) {
            setCandidates([]);
            return;
        }
        setCandidatesLoading(true);
        try {
            const { data } = await axiosInstance.get(`/v1/actions/ozon/${actionId}/auto-add`, {
                params: { auto_add_date: date, fetch_all: true },
            });
            setCandidates(data.products || []);
        } catch (error) {
            setCandidates([]);
            message.error(
                error.response?.data?.detail ||
                    'Не удалось загрузить кандидатов на автодобавление'
            );
        } finally {
            setCandidatesLoading(false);
        }
    }, []);

    const loadCost = useCallback(async () => {
        try {
            const [itemsRes, catalogRes] = await Promise.all([
                axiosInstance.get('/v1/cost/items'),
                axiosInstance.get('/v1/cost/catalog'),
            ]);
            setCostItems(itemsRes.data || []);
            setCostCatalog(catalogRes.data || []);
        } catch (error) {
            message.error(
                error.response?.data?.detail || 'Не удалось загрузить расчёт себестоимости'
            );
        }
    }, []);

    useEffect(() => {
        loadActions();
    }, [loadActions]);

    useEffect(() => {
        loadCost();
    }, [loadCost]);

    useEffect(() => {
        const dates = selectedAction?.auto_add_dates || [];
        setAutoAddDate(dates[0] || '');
        setSelectedCandidateIds([]);
        setProductQuery('');
        setCandidateQuery('');
        setActiveTab(
            selectedAction?.has_auto_add && !selectedAction?.is_participating
                ? 'candidates'
                : 'products'
        );
    }, [selectedAction]);

    useEffect(() => {
        if (!selectedActionId) {
            setProducts([]);
            setCandidates([]);
            return undefined;
        }
        loadProducts(selectedActionId);
        return undefined;
    }, [loadProducts, selectedActionId]);

    useEffect(() => {
        if (!selectedActionId || !autoAddDate) {
            setCandidates([]);
            return undefined;
        }
        loadCandidates(selectedActionId, autoAddDate);
        return undefined;
    }, [autoAddDate, loadCandidates, selectedActionId]);

    const filteredActions = useMemo(() => {
        const query = actionQuery.trim().toLowerCase();
        return actions.filter((item) => {
            if (actionFilter === 'participating' && !item.is_participating) return false;
            if (actionFilter === 'auto' && !item.has_auto_add) return false;
            if (!query) return true;
            return (
                String(item.title || '').toLowerCase().includes(query) ||
                String(item.id || '').includes(query) ||
                String(item.action_type || '').toLowerCase().includes(query)
            );
        });
    }, [actionFilter, actionQuery, actions]);

    const filteredProducts = useMemo(() => {
        const query = productQuery.trim().toLowerCase();
        if (!query) return products;
        return products.filter((item) =>
            [item.offer_id, item.name, item.product_id, item.sku]
                .filter((value) => value !== null && value !== undefined)
                .some((value) => String(value).toLowerCase().includes(query))
        );
    }, [productQuery, products]);

    const filteredCandidates = useMemo(() => {
        const query = candidateQuery.trim().toLowerCase();
        if (!query) return candidates;
        return candidates.filter((item) =>
            [item.offer_id, item.name, item.product_id, item.sku]
                .filter((value) => value !== null && value !== undefined)
                .some((value) => String(value).toLowerCase().includes(query))
        );
    }, [candidateQuery, candidates]);

    const deleteCandidates = async (productIds) => {
        if (!selectedActionId || !autoAddDate || !productIds.length) return;
        setDeleting(true);
        try {
            const { data } = await axiosInstance.post(
                `/v1/actions/ozon/${selectedActionId}/auto-add/delete`,
                {
                    auto_add_date: autoAddDate,
                    product_ids: productIds.map(String),
                }
            );
            const deleted = data.deleted_count ?? (data.product_ids || []).length;
            message.success(
                deleted
                    ? `Удалено из автодобавления: ${deleted}`
                    : 'Ozon не подтвердил удаление выбранных товаров'
            );
            setSelectedCandidateIds([]);
            await loadCandidates(selectedActionId, autoAddDate);
            await loadActions();
        } catch (error) {
            message.error(
                error.response?.data?.detail || 'Не удалось удалить товары из автодобавления'
            );
        } finally {
            setDeleting(false);
        }
    };

    const resetProductPrice = async (record) => {
        if (!selectedActionId || (!record?.product_id && !record?.offer_id)) return;
        const key = String(record.product_id || record.offer_id);
        setResettingProductKey(key);
        try {
            const { data } = await axiosInstance.post(
                `/v1/actions/ozon/${selectedActionId}/products/reset-price`,
                {
                    product_id: record.product_id || null,
                    offer_id: record.offer_id || '',
                }
            );
            message.success(
                `Предельная цена ${data.offer_id || record.offer_id || ''} установлена: ${formatMoney(
                    data.price
                )}`
            );
            await loadProducts(selectedActionId);
            if (autoAddDate) {
                await loadCandidates(selectedActionId, autoAddDate);
            }
            await loadActions();
        } catch (error) {
            message.error(
                error.response?.data?.detail || 'Не удалось вернуть исходную цену'
            );
        } finally {
            setResettingProductKey('');
        }
    };

    const productColumns = [
        {
            title: 'Товар',
            key: 'product',
            width: 320,
            onCell: () => ({ className: 'ozon-actions-product-cell' }),
            render: (_, record) => <ProductCell record={record} />,
        },
        {
            title: 'ID',
            dataIndex: 'product_id',
            key: 'product_id',
            width: 120,
            render: (value) => value || '—',
        },
        sellerPriceColumn,
        {
            title: PRICE_OZON_TITLE,
            dataIndex: 'price',
            key: 'price',
            width: 120,
            render: formatMoney,
        },
        {
            title: 'Цена акции',
            dataIndex: 'action_price',
            key: 'action_price',
            width: 130,
            render: formatMoney,
        },
        {
            title: DISCOUNT_OZON_TITLE,
            dataIndex: 'discount_percent',
            key: 'discount_percent',
            width: 110,
            render: (value) => (value == null ? '—' : `${value}%`),
        },
        realDiscountColumn,
        ...profitLossColumns(costItems, costCatalog),
        {
            title: 'Макс. цена',
            dataIndex: 'max_action_price',
            key: 'max_action_price',
            width: 130,
            render: formatMoney,
        },
        {
            title: 'Добавлен',
            dataIndex: 'add_mode',
            key: 'add_mode',
            width: 110,
            render: (value) => {
                const meta = addModeMeta[value] || { label: value || '—', color: 'default' };
                return <Tag color={meta.color}>{meta.label}</Tag>;
            },
        },
        warehouseStockColumn,
        {
            title: 'Карантин',
            dataIndex: 'is_quarantined',
            key: 'is_quarantined',
            width: 110,
            render: (value) =>
                value ? <Tag color="orange">В карантине</Tag> : <Tag>Нет</Tag>,
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 90,
            fixed: 'right',
            align: 'center',
            render: (_, record) => {
                const key = String(record.product_id || record.offer_id || '');
                const hasSeller = record.seller_price != null;
                const canReset = Boolean(record.product_id || record.offer_id);
                const disabled = !canReset || !hasSeller || Boolean(resettingProductKey);
                const button = (
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        loading={resettingProductKey === key}
                        disabled={disabled}
                    />
                );
                if (!hasSeller) {
                    return (
                        <Tooltip title="Нет исходной цены в Себестоимости">
                            <span>{button}</span>
                        </Tooltip>
                    );
                }
                return (
                    <Popconfirm
                        title="Удалить товар из акции?"
                        description={`Предельная цена станет равной исходной: ${formatMoney(
                            record.seller_price
                        )}`}
                        okText="Удалить"
                        cancelText="Отмена"
                        disabled={disabled}
                        onConfirm={() => resetProductPrice(record)}
                    >
                        <Tooltip title="Удалить">{button}</Tooltip>
                    </Popconfirm>
                );
            },
        },
    ];

    const candidateColumns = [
        {
            title: 'Товар',
            key: 'product',
            width: 320,
            onCell: () => ({ className: 'ozon-actions-product-cell' }),
            render: (_, record) => <ProductCell record={record} />,
        },
        {
            title: 'ID',
            dataIndex: 'product_id',
            key: 'product_id',
            width: 120,
            render: (value) => value || '—',
        },
        sellerPriceColumn,
        {
            title: PRICE_OZON_TITLE,
            dataIndex: 'price',
            key: 'price',
            width: 120,
            render: formatMoney,
        },
        {
            title: 'Цена автодобавления',
            dataIndex: 'action_price',
            key: 'action_price',
            width: 170,
            render: formatMoney,
        },
        {
            title: DISCOUNT_OZON_TITLE,
            dataIndex: 'discount_percent',
            key: 'discount_percent',
            width: 110,
            render: (value) => (value == null ? '—' : `${value}%`),
        },
        realDiscountColumn,
        ...profitLossColumns(costItems, costCatalog),
        warehouseStockColumn,
        {
            title: 'Кол-во',
            dataIndex: 'quantity_to_auto_add',
            key: 'quantity_to_auto_add',
            width: 90,
            render: (value) => (value == null ? '—' : value),
        },
        {
            title: 'Карантин',
            dataIndex: 'will_be_quarantined',
            key: 'will_be_quarantined',
            width: 120,
            render: (value) =>
                value ? <Tag color="orange">Попадёт</Tag> : <Tag>Нет</Tag>,
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 220,
            fixed: 'right',
            render: (_, record) => {
                const key = String(record.product_id || record.offer_id || '');
                const hasSeller = record.seller_price != null;
                const canReset = Boolean(record.product_id || record.offer_id);
                const resetDisabled = !canReset || !hasSeller || Boolean(resettingProductKey);
                const resetButton = (
                    <Button
                        type="link"
                        icon={<RollbackOutlined />}
                        loading={resettingProductKey === key}
                        disabled={resetDisabled}
                    >
                        Вернуть исходную цену
                    </Button>
                );
                return (
                    <Space direction="vertical" size={0}>
                        {hasSeller ? (
                            <Popconfirm
                                title="Вернуть исходную цену?"
                                description={`Предельная цена станет равной исходной: ${formatMoney(
                                    record.seller_price
                                )}`}
                                okText="Вернуть"
                                cancelText="Отмена"
                                disabled={resetDisabled}
                                onConfirm={() => resetProductPrice(record)}
                            >
                                {resetButton}
                            </Popconfirm>
                        ) : (
                            <Tooltip title="Нет исходной цены в Себестоимости">
                                <span>{resetButton}</span>
                            </Tooltip>
                        )}
                        <Popconfirm
                            title="Убрать товар из автодобавления?"
                            okText="Убрать"
                            cancelText="Отмена"
                            disabled={!record.product_id || deleting}
                            onConfirm={() => deleteCandidates([record.product_id])}
                        >
                            <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                disabled={!record.product_id || deleting}
                            >
                                Убрать
                            </Button>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    const actionColumns = [
        {
            title: 'Акция',
            dataIndex: 'title',
            key: 'title',
            width: 280,
            render: (value, record) => (
                <div>
                    <Typography.Text strong>{value || 'Без названия'}</Typography.Text>
                    <div className="text-xs text-slate-500">ID {record.id}</div>
                </div>
            ),
        },
        {
            title: 'Период',
            key: 'period',
            width: 220,
            render: (_, record) => (
                <div className="text-xs leading-5 text-slate-600">
                    <div>{formatDate(record.date_start)}</div>
                    <div>{formatDate(record.date_end)}</div>
                </div>
            ),
        },
        {
            title: 'В акции',
            dataIndex: 'participating_products_count',
            key: 'participating_products_count',
            width: 90,
        },
        {
            title: 'Кандидаты',
            dataIndex: 'potential_products_count',
            key: 'potential_products_count',
            width: 110,
        },
        {
            title: 'Автодобавление',
            key: 'auto_add',
            width: 160,
            render: (_, record) =>
                record.has_auto_add ? (
                    <Tag color="blue">{record.auto_add_dates.length} волны</Tag>
                ) : (
                    <Tag>Нет</Tag>
                ),
        },
        {
            title: 'Участие',
            dataIndex: 'is_participating',
            key: 'is_participating',
            width: 120,
            render: (value) =>
                value ? <Tag color="green">Участвуем</Tag> : <Tag>Нет</Tag>,
        },
    ];

    const visibleActionColumns = applyColumnLayout(
        actionColumns,
        tableSettings.actions,
        (key, width) => updateColumnWidth('actions', key, width, false),
        (key, width) => updateColumnWidth('actions', key, width, true),
        (key) => resetColumnWidth('actions', key)
    );
    const visibleProductColumns = applyColumnLayout(
        productColumns,
        tableSettings.products,
        (key, width) => updateColumnWidth('products', key, width, false),
        (key, width) => updateColumnWidth('products', key, width, true),
        (key) => resetColumnWidth('products', key)
    );
    const visibleCandidateColumns = applyColumnLayout(
        candidateColumns,
        tableSettings.candidates,
        (key, width) => updateColumnWidth('candidates', key, width, false),
        (key, width) => updateColumnWidth('candidates', key, width, true),
        (key) => resetColumnWidth('candidates', key)
    );
    const actionScrollX = visibleActionColumns.reduce(
        (sum, column) => sum + (Number(column.width) || 80),
        0
    );
    const productScrollX = visibleProductColumns.reduce(
        (sum, column) => sum + (Number(column.width) || 80),
        0
    );
    const candidateScrollX = visibleCandidateColumns.reduce(
        (sum, column) => sum + (Number(column.width) || 80),
        0
    );

    return (
        <div className="w-full space-y-4">
            <Card>
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <Typography.Title level={2} style={{ margin: 0 }}>
                            Акции OZON
                        </Typography.Title>
                        <Typography.Text type="secondary">
                            Товары в акциях и кандидаты на автодобавление из Seller API
                        </Typography.Text>
                    </div>
                    <Button
                        icon={<ReloadOutlined />}
                        loading={actionsLoading}
                        onClick={loadActions}
                    >
                        Обновить
                    </Button>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-xl bg-slate-50 px-4 py-3">
                        <div className="text-xs text-slate-500">Всего акций</div>
                        <div className="text-xl font-semibold">{summary.total}</div>
                    </div>
                    <div className="rounded-xl bg-emerald-50 px-4 py-3">
                        <div className="text-xs text-emerald-700">Участвуем</div>
                        <div className="text-xl font-semibold text-emerald-800">
                            {summary.participating}
                        </div>
                    </div>
                    <div className="rounded-xl bg-sky-50 px-4 py-3">
                        <div className="text-xs text-sky-700">С автодобавлением</div>
                        <div className="text-xl font-semibold text-sky-800">
                            {summary.with_auto_add}
                        </div>
                    </div>
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                    <Input
                        allowClear
                        prefix={<SearchOutlined />}
                        placeholder="Название или ID акции"
                        value={actionQuery}
                        onChange={(event) => setActionQuery(event.target.value)}
                        style={{ maxWidth: 320 }}
                    />
                    <Select
                        value={actionFilter}
                        onChange={setActionFilter}
                        style={{ width: 220 }}
                        options={[
                            { value: 'all', label: 'Все акции' },
                            { value: 'participating', label: 'Только с участием' },
                            { value: 'auto', label: 'С автодобавлением' },
                        ]}
                    />
                    <ColumnsMenu
                        options={ACTION_COLUMN_OPTIONS}
                        hiddenColumns={tableSettings.actions.hiddenColumns}
                        onChange={(hiddenColumns) => setHiddenColumns('actions', hiddenColumns)}
                    />
                </div>

                <div className="mt-4 ozon-actions-table-wrap">
                    <Table
                        rowKey={(record) => record.id}
                        columns={visibleActionColumns}
                        dataSource={filteredActions}
                        loading={actionsLoading}
                        size="small"
                        tableLayout="fixed"
                        scroll={{ x: `max(100%, ${actionScrollX}px)` }}
                        components={{ header: { cell: ResizableTitle } }}
                        pagination={{ pageSize: 8, hideOnSinglePage: true }}
                        locale={{ emptyText: 'Акции не найдены' }}
                        rowClassName={(record) =>
                            record.id === selectedActionId ? 'ant-table-row-selected' : ''
                        }
                        onRow={(record) => ({
                            onClick: () => setSelectedActionId(record.id),
                            style: { cursor: 'pointer' },
                        })}
                    />
                </div>
            </Card>

            <Card>
                {selectedAction ? (
                    <>
                        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <GiftOutlined />
                                    <Typography.Title level={4} style={{ margin: 0 }}>
                                        {selectedAction.title}
                                    </Typography.Title>
                                </div>
                                <Typography.Text type="secondary">
                                    {formatDate(selectedAction.date_start)} —{' '}
                                    {formatDate(selectedAction.date_end)}
                                    {selectedAction.action_type
                                        ? ` · ${selectedAction.action_type}`
                                        : ''}
                                </Typography.Text>
                            </div>
                            <Space wrap>
                                {selectedAction.is_participating && (
                                    <Tag color="green">Участвуем</Tag>
                                )}
                                {selectedAction.is_voucher_action && (
                                    <Tag color="purple">Промокод</Tag>
                                )}
                                {selectedAction.freeze_date && (
                                    <Tag color="red">
                                        Заморозка {formatDate(selectedAction.freeze_date)}
                                    </Tag>
                                )}
                            </Space>
                        </div>

                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            items={[
                                {
                                    key: 'products',
                                    label: `Товары в акции (${products.length})`,
                                    children: (
                                        <div>
                                            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                                                <Input
                                                    allowClear
                                                    prefix={<SearchOutlined />}
                                                    placeholder="Артикул, название или ID"
                                                    value={productQuery}
                                                    onChange={(event) =>
                                                        setProductQuery(event.target.value)
                                                    }
                                                    style={{ maxWidth: 320 }}
                                                />
                                                <Space wrap>
                                                    <ColumnsMenu
                                                        options={PRODUCT_COLUMN_OPTIONS}
                                                        hiddenColumns={
                                                            tableSettings.products.hiddenColumns
                                                        }
                                                        onChange={(hiddenColumns) =>
                                                            setHiddenColumns('products', hiddenColumns)
                                                        }
                                                    />
                                                    <Button
                                                        icon={<ReloadOutlined />}
                                                        loading={productsLoading}
                                                        onClick={() => loadProducts(selectedActionId)}
                                                    >
                                                        Обновить товары
                                                    </Button>
                                                </Space>
                                            </div>
                                            <div className="ozon-actions-table-wrap">
                                            <Table
                                                rowKey={(record) =>
                                                    record.product_id || record.offer_id
                                                }
                                                columns={visibleProductColumns}
                                                dataSource={filteredProducts}
                                                loading={productsLoading}
                                                size="small"
                                                tableLayout="fixed"
                                                scroll={{ x: `max(100%, ${productScrollX}px)` }}
                                                components={{ header: { cell: ResizableTitle } }}
                                                locale={{ emptyText: 'В акции нет товаров' }}
                                                pagination={{
                                                    pageSize: 20,
                                                    showSizeChanger: true,
                                                    pageSizeOptions: [20, 50, 100],
                                                    showTotal: (total) => `Товаров: ${total}`,
                                                }}
                                            />
                                            </div>
                                        </div>
                                    ),
                                },
                                {
                                    key: 'candidates',
                                    label: `Кандидаты на добавление (${candidates.length})`,
                                    children: (
                                        <div>
                                            {!selectedAction.has_auto_add ? (
                                                <Alert
                                                    type="info"
                                                    showIcon
                                                    message="Для этой акции нет запланированного автодобавления"
                                                />
                                            ) : (
                                                <>
                                                    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                                                        <Space wrap>
                                                            <Select
                                                                value={autoAddDate}
                                                                onChange={setAutoAddDate}
                                                                style={{ minWidth: 240 }}
                                                                options={(
                                                                    selectedAction.auto_add_dates ||
                                                                    []
                                                                ).map((date) => ({
                                                                    value: date,
                                                                    label: `Волна ${formatDate(date)}`,
                                                                }))}
                                                            />
                                                            <Input
                                                                allowClear
                                                                prefix={<SearchOutlined />}
                                                                placeholder="Артикул, название или ID"
                                                                value={candidateQuery}
                                                                onChange={(event) =>
                                                                    setCandidateQuery(
                                                                        event.target.value
                                                                    )
                                                                }
                                                                style={{ maxWidth: 280 }}
                                                            />
                                                        </Space>
                                                        <Space wrap>
                                                            <ColumnsMenu
                                                                options={CANDIDATE_COLUMN_OPTIONS}
                                                                hiddenColumns={
                                                                    tableSettings.candidates
                                                                        .hiddenColumns
                                                                }
                                                                onChange={(hiddenColumns) =>
                                                                    setHiddenColumns(
                                                                        'candidates',
                                                                        hiddenColumns
                                                                    )
                                                                }
                                                            />
                                                            <Button
                                                                icon={<ReloadOutlined />}
                                                                loading={candidatesLoading}
                                                                onClick={() =>
                                                                    loadCandidates(
                                                                        selectedActionId,
                                                                        autoAddDate
                                                                    )
                                                                }
                                                            >
                                                                Обновить
                                                            </Button>
                                                            <Popconfirm
                                                                title={`Убрать выбранные товары из автодобавления (${selectedCandidateIds.length})?`}
                                                                okText="Убрать"
                                                                cancelText="Отмена"
                                                                disabled={!selectedCandidateIds.length}
                                                                onConfirm={() =>
                                                                    deleteCandidates(
                                                                        selectedCandidateIds
                                                                    )
                                                                }
                                                            >
                                                            <Button
                                                                danger
                                                                icon={<DeleteOutlined />}
                                                                disabled={
                                                                    !selectedCandidateIds.length
                                                                }
                                                                loading={deleting}
                                                            >
                                                                Убрать выбранные
                                                                {selectedCandidateIds.length
                                                                    ? ` (${selectedCandidateIds.length})`
                                                                    : ''}
                                                            </Button>
                                                            </Popconfirm>
                                                        </Space>
                                                    </div>
                                                    <div className="ozon-actions-table-wrap">
                                                    <Table
                                                        rowKey={(record) =>
                                                            record.product_id || record.offer_id
                                                        }
                                                        columns={visibleCandidateColumns}
                                                        dataSource={filteredCandidates}
                                                        loading={candidatesLoading}
                                                        size="small"
                                                        tableLayout="fixed"
                                                        scroll={{ x: `max(100%, ${candidateScrollX}px)` }}
                                                        components={{ header: { cell: ResizableTitle } }}
                                                        locale={{
                                                            emptyText:
                                                                'Нет товаров в автодобавлении',
                                                        }}
                                                        rowSelection={{
                                                            selectedRowKeys:
                                                                selectedCandidateIds,
                                                            onChange: setSelectedCandidateIds,
                                                            getCheckboxProps: (record) => ({
                                                                disabled: !record.product_id,
                                                            }),
                                                        }}
                                                        pagination={{
                                                            pageSize: 20,
                                                            showSizeChanger: true,
                                                            pageSizeOptions: [20, 50, 100],
                                                            showTotal: (total) =>
                                                                `Кандидатов: ${total}`,
                                                        }}
                                                    />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ),
                                },
                            ]}
                        />
                    </>
                ) : (
                    <Typography.Text type="secondary">
                        Выберите акцию, чтобы увидеть товары и кандидатов на добавление
                    </Typography.Text>
                )}
            </Card>
        </div>
    );
};

export default OzonActions;
